import { Box, Checkbox, FormControl, IconButton, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useCallback, useEffect, useState } from 'react';
import { MdCheckBox, MdCheckBoxOutlineBlank, MdClear, MdDelete } from 'react-icons/md';

import SearchWithFilters from 'components/SearchWithFilters';
import { MultiSelectDoneButton } from 'GeminiViewerComponent/components/MultiSelectDoneButton';
import { debounce } from 'GeminiViewerComponent/helpers/lodashUtils';
import { ContentTypes, LinkTypes } from 'helpers/enumerations';

const SelectExistingContentFilters = ({
    contentPayload,
    setContentPayload,
    searchStringState,
    setSearchStringState,
    contentType,
}) => {
    const [contentTypeFilter, setContentTypeFilter] = useState([]);
    const [contentTypeOptions, setContentTypeOptions] = useState([]);

    useEffect(() => {
        if (contentType === 'video') {
            setContentTypeOptions(LinkTypes.Video.contentTypes);
        } else if (contentType === 'image') {
            setContentTypeOptions(LinkTypes.Image.contentTypes);
        } else if (contentType === 'pdf') {
            setContentTypeOptions([ContentTypes.Pdf]);
        } else if (contentType === 'learning') {
            // Only HTML and ZIP are valid learning item formats — restrict the filter options.
            setContentTypeOptions([ContentTypes.Html, ContentTypes.Zip]);
        } else {
            setContentTypeOptions(
                Object.values(ContentTypes).filter(
                    (x) => x.id !== ContentTypes.Procedure.id && x.id !== ContentTypes.Schoox.id
                )
            );
        }
    }, [contentType]);

    const getContentTypeIds = (contentTypeIds) => {
        if (contentTypeIds && contentTypeIds?.length > 0) {
            return [...contentTypeIds];
        } else {
            if (contentType === 'video') {
                return LinkTypes.Video.contentTypes.map((link) => link.id);
            } else if (contentType === 'image') {
                return LinkTypes.Image.contentTypes.map((link) => link.id);
            } else if (contentType === 'learning') {
                // Default to HTML and ZIP when no specific type is selected.
                return [ContentTypes.Html.id, ContentTypes.Zip.id];
            } else {
                return contentTypeOptions.map((ct) => ct.id);
            }
        }
    };

    const debouncedSearch = useCallback(
        debounce((searchString, contentTypeIds) => {
            setContentPayload({
                ...contentPayload,
                reset: true,
                startIndex: 0,
                stopIndex: 19,
                contentTypeIds: getContentTypeIds(contentTypeIds),
                searchString: searchString,
            });
        }, 1000),
        [contentPayload]
    );

    const handleSearch = (searchString) => {
        setSearchStringState(searchString);
        const array = contentTypeFilter.map((item) => item.id);
        debouncedSearch(searchString, array);
    };

    const updateSearchString = (evt) => {
        let searchString = evt.target.value;
        handleSearch(searchString);
    };

    const clearFilter = useCallback(() => {
        if (contentTypeFilter && contentTypeFilter?.length > 0) {
            setContentPayload({
                reset: true,
                startIndex: 0,
                stopIndex: 19,
                contentTypeIds: getContentTypeIds(),
                searchString: searchStringState,
            });
            setContentTypeFilter([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentType, contentTypeFilter, searchStringState]);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {Object.keys(ContentTypes)?.length > 0 && (
                    <FormControl
                        variant="outlined"
                        fullWidth
                        style={{
                            position: 'relative',
                            width: '50%',
                        }}
                    >
                        <Autocomplete
                            sx={{
                                minWidth: '140px',
                            }}
                            limitTags={1}
                            multiple
                            variant="outline"
                            id="checkboxes-content-types"
                            options={contentTypeOptions}
                            clearIcon={<MdDelete fontSize="18" />}
                            onChange={(_, newInputValue) => {
                                setContentTypeFilter(newInputValue);
                                const array = newInputValue.map((item) => item.id);
                                debouncedSearch(searchStringState, array);
                            }}
                            PaperComponent={MultiSelectDoneButton}
                            disableCloseOnSelect
                            value={contentTypeFilter}
                            disableClearable
                            getOptionLabel={(option) => option.label}
                            renderOption={(props, option, { selected }) => (
                                <li
                                    {...props}
                                    style={{
                                        padding: '0',
                                    }}
                                >
                                    <Checkbox
                                        icon={<MdCheckBoxOutlineBlank className="react-icon" />}
                                        checkedIcon={<MdCheckBox className="react-icon" />}
                                        style={{
                                            marginRight: 8,
                                        }}
                                        checked={selected}
                                    />
                                    {option.label}
                                </li>
                            )}
                            renderInput={(params) => (
                                <>
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Content Type"
                                        placeholder="Content Type"
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            right: '25px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            fontSize: '20px',
                                        }}
                                        className={'clearSelect'}
                                    >
                                        <IconButton onClick={clearFilter}>
                                            <MdClear />
                                        </IconButton>
                                    </Box>
                                </>
                            )}
                        />
                    </FormControl>
                )}

                <SearchWithFilters onChange={(evt) => updateSearchString(evt)} filter={false}></SearchWithFilters>
            </div>
        </div>
    );
};

export { SelectExistingContentFilters };
